import type { CreateFormData } from "@/components/create/types";
import { calculateHasPrompt } from "@/stores/utils";
import type { DesignBrief, DressConcept } from "@/types/createAgent";
import { baseBriefFromForm } from "@/lib/agent/briefForm";
import { compileCurrentDesignSpec } from "@/lib/agent/briefForm";
import { createDesignSpecSnapshot } from "@/lib/agent/designSpecSnapshot";
import { hasBackUploadReference } from "@/lib/agent/actionReadinessGuards";
import type { RenderedDesignSpec } from "@/types/generatedImage";
import {
  clearReversedRejections,
  valueMatchesRejection,
  withoutRemovedEntries,
} from "@/lib/agent/rejectionMatching";
import { getSideRelevantDesignChanges } from "./previewFreshness";

export interface EditableDesignState {
  form: {
    data: CreateFormData;
    showValidationError: boolean;
    hasPrompt?: boolean;
  };
  agent: {
    designBrief: DesignBrief | null;
    activeConcept: DressConcept | null;
  };
  generation: {
    images: {
      front: {
        url: string | null;
        path?: string | null;
        designSpec?: RenderedDesignSpec;
      };
      back: {
        url: string | null;
        path?: string | null;
        designSpec?: RenderedDesignSpec;
      };
    };
  };
}

export type EditableDesignDetailField =
  | "silhouette"
  | "color"
  | "fabric"
  | "length"
  | "neckline"
  | "sleeve"
  | "waistline"
  | "back"
  | "mustHave"
  | "avoid";

const CORE_DETAIL_PATHS: Record<string, EditableDesignDetailField> = {
  "visual.silhouette": "silhouette",
  "visual.primaryColor": "color",
  "visual.fabric": "fabric",
  "visual.length": "length",
  "visual.neckline": "neckline",
  "visual.sleeve": "sleeve",
  "visual.waistline": "waistline",
  "constraints.mustHave": "mustHave",
  "constraints.avoid": "avoid",
};

const PATH_BY_CORE_DETAIL: Record<EditableDesignDetailField, string> = {
  silhouette: "visual.silhouette",
  color: "visual.primaryColor",
  fabric: "visual.fabric",
  length: "visual.length",
  neckline: "visual.neckline",
  sleeve: "visual.sleeve",
  waistline: "visual.waistline",
  back: "visual.backPrompt",
  mustHave: "constraints.mustHave",
  avoid: "constraints.avoid",
};

const SUPPLEMENTAL_DETAIL_PATHS = new Set([
  "occasion",
  "eventDate",
  "wearerContext",
  "visual.prompt",
  "visual.backPrompt",
  "visual.backStyle",
  "visual.closure",
  "visual.accentColors",
  "visual.sleeveAddons",
  "visual.mood",
  "visual.details",
  "constraints.comfort",
  "constraints.mobility",
  "constraints.bodyConcerns",
  "constraints.modesty",
  "constraints.coverage",
  "constraints.support",
  "constraints.opacity",
  "constraints.accessibility",
  "constraints.cultural",
  "constraints.construction",
  "constraints.budget",
  "referenceGuidance",
]);

export function canEditDesignDetailPath(path: string): boolean {
  return path in CORE_DETAIL_PATHS || SUPPLEMENTAL_DETAIL_PATHS.has(path);
}

function uniqueTextList(value: string): string[] {
  const seen = new Set<string>();
  return value
    .split(/[\n,;]+/u)
    .map(item => item.trim())
    .filter(item => {
      const normalized = item.toLocaleLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
}

function setClearedField(
  brief: DesignBrief,
  field: NonNullable<DesignBrief["clearedFields"]>[number],
  cleared: boolean
): void {
  const fields = new Set(brief.clearedFields ?? []);
  if (cleared) fields.add(field);
  else fields.delete(field);
  brief.clearedFields = fields.size > 0 ? Array.from(fields) : undefined;
}

function setConfirmedProvenance(
  brief: DesignBrief,
  field: string,
  present: boolean
): void {
  if (!present) {
    if (brief.provenance) delete brief.provenance[field];
    return;
  }
  brief.provenance = {
    ...(brief.provenance ?? {}),
    [field]: { source: "explicit_user", confirmed: true },
  };
}

function setConceptDecision(
  concept: DressConcept | null | undefined,
  field: keyof DressConcept["visualDecisions"],
  value: string | undefined
): void {
  if (!concept) return;
  if (value) concept.visualDecisions[field] = value as never;
  else delete concept.visualDecisions[field];
}

/** Track only edits after an unknown-contract image, without inventing its render history. */
function captureLegacyPreviewEditBaseline(
  state: EditableDesignState,
  path: string
): void {
  const images = state.generation.images;
  const editedSides = (["front", "back"] as const).filter(side => {
    const image = images[side];
    return Boolean(
      image.url &&
      !image.designSpec &&
      getSideRelevantDesignChanges(
        [{ path, renderedValue: undefined, draftValue: undefined }],
        side
      ).length
    );
  });
  if (!editedSides.length) return;

  const baseline = createDesignSpecSnapshot(
    compileCurrentDesignSpec(state.agent.designBrief, state.form.data, {
      hasFrontImage: Boolean(images.front.url),
      hasBackImage: Boolean(images.back.url),
    })
  );
  const baselines = state.form.data.legacyPreviewEditBaselines ?? {};
  for (const side of editedSides) {
    const image = images[side];
    const imageKey = image.path || image.url!;
    if (baselines[side]?.imageKey !== imageKey) {
      baselines[side] = { imageKey, draft: baseline };
    }
  }
  state.form.data.legacyPreviewEditBaselines = baselines;
}

/** The compact inspector saves one text row at a time into the canonical draft. */
export function applyDesignDetailPathEdit(
  state: EditableDesignState,
  path: string,
  rawValue: string
): void {
  const coreField = CORE_DETAIL_PATHS[path];
  if (coreField) {
    applyDesignDetailEdit(state, coreField, rawValue);
    return;
  }
  if (!SUPPLEMENTAL_DETAIL_PATHS.has(path)) return;

  const value = rawValue.trim();
  if (
    path === "constraints.modesty" &&
    value !== "" &&
    value !== "modest" &&
    value !== "balanced" &&
    value !== "revealing"
  )
    return;

  captureLegacyPreviewEditBaseline(state, path);

  const brief =
    state.agent.designBrief ??
    baseBriefFromForm(state.form.data, {
      hasFrontImage: Boolean(state.generation.images.front.url),
      hasBackImage: Boolean(state.generation.images.back.url),
    });
  state.agent.designBrief = brief;
  const items = uniqueTextList(value);
  const provenancePath = path;
  const setList = (target: string[]) => {
    target.splice(0, target.length, ...items);
  };

  switch (path) {
    case "occasion":
      brief.occasion = value || undefined;
      state.form.data.preset_type = value;
      setClearedField(brief, "occasion", !value);
      break;
    case "eventDate":
      brief.event ??= {};
      brief.event.date = value || undefined;
      setClearedField(brief, "eventDate", !value);
      break;
    case "wearerContext":
      brief.event ??= {};
      brief.event.wearerContext = items;
      break;
    case "visual.prompt":
      brief.front.prompt = value || undefined;
      state.form.data.prompt = value;
      if (state.form.hasPrompt !== undefined)
        state.form.hasPrompt = calculateHasPrompt(state.form.data);
      setClearedField(brief, "prompt", !value);
      break;
    case "visual.backPrompt": {
      const previousPrompt = brief.back.prompt;
      brief.back.prompt = value || undefined;
      brief.back.details = [
        ...(value ? [value] : []),
        ...brief.back.details.filter(detail => detail !== previousPrompt),
      ];
      state.form.data.backPrompt = value;
      setConceptDecision(state.agent.activeConcept, "back", value || undefined);
      setConceptDecision(brief.activeConcept, "back", value || undefined);
      setClearedField(brief, "backPrompt", !value);
      break;
    }
    case "visual.backStyle":
      brief.back.style = value || undefined;
      setClearedField(brief, "backStyle", !value);
      break;
    case "visual.closure":
      brief.back.closure = value || undefined;
      setClearedField(brief, "backClosure", !value);
      break;
    case "visual.accentColors":
      brief.constraints.color.accents = items;
      break;
    case "visual.sleeveAddons":
      setList(brief.front.sleeveAddons);
      state.form.data.sleeveAddons = items;
      break;
    case "visual.mood":
      setList(brief.front.mood);
      break;
    case "visual.details":
      setList(brief.front.details);
      break;
    case "referenceGuidance":
      brief.constraints.referenceGuidance = items;
      break;
    case "constraints.modesty":
      brief.constraints.modesty = value
        ? (value as "modest" | "balanced" | "revealing")
        : undefined;
      state.form.data.modestyPreference = brief.constraints.modesty;
      setClearedField(brief, "modesty", !value);
      break;
    default: {
      const key = path.slice("constraints.".length) as
        | "comfort"
        | "mobility"
        | "bodyConcerns"
        | "coverage"
        | "support"
        | "opacity"
        | "accessibility"
        | "cultural"
        | "construction"
        | "budget";
      brief.constraints[key] = items;
    }
  }

  if (
    !state.generation.images.back.url &&
    (path === "visual.backPrompt" ||
      path === "visual.backStyle" ||
      path === "visual.closure")
  ) {
    state.form.data.generateBack = Boolean(
      brief.back.prompt?.trim() ||
      brief.back.style?.trim() ||
      brief.back.closure?.trim() ||
      hasBackUploadReference(state.form.data)
    );
  }

  if (
    value &&
    (path === "visual.backPrompt" ||
      path === "visual.backStyle" ||
      path === "visual.closure")
  ) {
    clearReversedRejections(brief, value);
    if (brief.constraints.avoid.length === 0)
      setConfirmedProvenance(brief, "constraints.avoid", false);
  }
  setConfirmedProvenance(brief, provenancePath, Boolean(value));
  brief.updatedAt = new Date().toISOString();
  state.form.showValidationError = false;
}

/** Shared Details edits for the browser and customer lab. Mutates the supplied draft. */
export function applyDesignDetailEdit(
  state: EditableDesignState,
  field: EditableDesignDetailField,
  rawValue: string
) {
  captureLegacyPreviewEditBaseline(state, PATH_BY_CORE_DETAIL[field]);
  const value = rawValue.trim();
  const brief =
    state.agent.designBrief ??
    baseBriefFromForm(state.form.data, {
      hasFrontImage: Boolean(state.generation.images.front.url),
      hasBackImage: Boolean(state.generation.images.back.url),
    });
  state.agent.designBrief = brief;

  const setConcepts = (
    conceptField: keyof DressConcept["visualDecisions"],
    nextValue: string | undefined
  ) => {
    setConceptDecision(state.agent.activeConcept, conceptField, nextValue);
    setConceptDecision(brief.activeConcept, conceptField, nextValue);
  };
  const setScalarMetadata = (
    tombstone: NonNullable<DesignBrief["clearedFields"]>[number],
    provenanceField: string
  ) => {
    setClearedField(brief, tombstone, !value);
    setConfirmedProvenance(brief, provenanceField, Boolean(value));
    if (value) {
      clearReversedRejections(brief, value);
      if (brief.constraints.avoid.length === 0) {
        setConfirmedProvenance(brief, "constraints.avoid", false);
      }
    }
  };

  switch (field) {
    case "silhouette":
      brief.front.silhouette = value || undefined;
      state.form.data.silhouette = value;
      setConcepts("silhouette", value || undefined);
      setScalarMetadata("silhouette", "visual.silhouette");
      break;
    case "color": {
      brief.front.color = value || undefined;
      brief.constraints.color.primary = value || undefined;
      state.form.data.color = value;
      state.form.data.colorHex = "";
      setConcepts("color", value || undefined);
      setScalarMetadata("primaryColor", "visual.primaryColor");
      if (value) {
        const preferred = new Map(
          brief.constraints.color.preferred.map(
            item => [item.toLocaleLowerCase(), item] as const
          )
        );
        preferred.set(value.toLocaleLowerCase(), value);
        brief.constraints.color.preferred = Array.from(preferred.values());
        brief.constraints.color.accents = (
          brief.constraints.color.accents ?? []
        ).filter(
          item => item.toLocaleLowerCase() !== value.toLocaleLowerCase()
        );
      }
      break;
    }
    case "fabric":
      brief.front.fabric = value || undefined;
      state.form.data.fabric = value;
      setConcepts("fabric", value || undefined);
      setScalarMetadata("fabric", "visual.fabric");
      break;
    case "length":
      brief.front.length = value || undefined;
      state.form.data.length = value;
      setConcepts("length", value || undefined);
      setScalarMetadata("length", "visual.length");
      break;
    case "neckline":
      brief.front.neckline = value || undefined;
      state.form.data.neckline = value;
      setConcepts("neckline", value || undefined);
      setScalarMetadata("neckline", "visual.neckline");
      break;
    case "sleeve":
      brief.front.sleeve = value || undefined;
      state.form.data.sleeve = value;
      setConcepts("sleeve", value || undefined);
      setScalarMetadata("sleeve", "visual.sleeve");
      break;
    case "waistline":
      brief.front.waistline = value || undefined;
      state.form.data.waistline = value;
      setConcepts("waistline", value || undefined);
      setScalarMetadata("waistline", "visual.waistline");
      break;
    case "back":
      // An actual Details edit requests a new back even for a legacy front
      // whose render contract is unknown. Hydration never calls this action.
      brief.back.prompt = value || undefined;
      brief.back.style = undefined;
      brief.back.closure = undefined;
      brief.back.details = value ? [value] : [];
      state.form.data.backPrompt = value;
      setConcepts("back", value || undefined);
      setScalarMetadata("backPrompt", "visual.backPrompt");
      setClearedField(brief, "backStyle", true);
      setClearedField(brief, "backClosure", true);
      setConfirmedProvenance(brief, "visual.backStyle", false);
      setConfirmedProvenance(brief, "visual.closure", false);
      if (!state.generation.images.back.url)
        state.form.data.generateBack = Boolean(
          value || hasBackUploadReference(state.form.data)
        );
      break;
    case "mustHave": {
      const items = uniqueTextList(value);
      brief.constraints.mustHave = items;
      setConfirmedProvenance(brief, "constraints.mustHave", items.length > 0);
      for (const item of items) clearReversedRejections(brief, item);
      if (brief.constraints.avoid.length === 0) {
        setConfirmedProvenance(brief, "constraints.avoid", false);
      }
      break;
    }
    case "avoid": {
      const previousAvoid = brief.constraints.avoid;
      const items = uniqueTextList(value);
      brief.constraints.avoid = items;
      brief.rejectedIdeas = uniqueTextList(
        [
          ...withoutRemovedEntries(brief.rejectedIdeas, previousAvoid),
          ...items,
        ].join("\n")
      );
      brief.constraints.color.rejected = withoutRemovedEntries(
        brief.constraints.color.rejected,
        previousAvoid
      );
      brief.constraints.mustHave = brief.constraints.mustHave.filter(
        mustHave =>
          !items.some(rejection => valueMatchesRejection(mustHave, rejection))
      );
      setConfirmedProvenance(brief, "constraints.avoid", items.length > 0);

      const isRejected = (candidate: string | undefined) =>
        Boolean(
          candidate &&
          items.some(rejection => valueMatchesRejection(candidate, rejection))
        );
      const clearRejectedScalar = (
        candidate: string | undefined,
        tombstone: NonNullable<DesignBrief["clearedFields"]>[number],
        provenanceField: string,
        clear: () => void
      ) => {
        if (!isRejected(candidate)) return;
        clear();
        setClearedField(brief, tombstone, true);
        setConfirmedProvenance(brief, provenanceField, false);
      };
      clearRejectedScalar(
        brief.front.silhouette,
        "silhouette",
        "visual.silhouette",
        () => {
          brief.front.silhouette = undefined;
          state.form.data.silhouette = "";
          setConcepts("silhouette", undefined);
        }
      );
      clearRejectedScalar(
        brief.constraints.color.primary ?? brief.front.color,
        "primaryColor",
        "visual.primaryColor",
        () => {
          brief.front.color = undefined;
          brief.constraints.color.primary = undefined;
          state.form.data.color = "";
          state.form.data.colorHex = "";
          setConcepts("color", undefined);
        }
      );
      for (const candidate of [
        {
          value: brief.front.fabric,
          tombstone: "fabric" as const,
          provenance: "visual.fabric",
          clear: () => {
            brief.front.fabric = undefined;
            state.form.data.fabric = "";
            setConcepts("fabric", undefined);
          },
        },
        {
          value: brief.front.length,
          tombstone: "length" as const,
          provenance: "visual.length",
          clear: () => {
            brief.front.length = undefined;
            state.form.data.length = "";
            setConcepts("length", undefined);
          },
        },
        {
          value: brief.front.neckline,
          tombstone: "neckline" as const,
          provenance: "visual.neckline",
          clear: () => {
            brief.front.neckline = undefined;
            state.form.data.neckline = "";
            setConcepts("neckline", undefined);
          },
        },
        {
          value: brief.front.sleeve,
          tombstone: "sleeve" as const,
          provenance: "visual.sleeve",
          clear: () => {
            brief.front.sleeve = undefined;
            state.form.data.sleeve = "";
            setConcepts("sleeve", undefined);
          },
        },
        {
          value: brief.front.waistline,
          tombstone: "waistline" as const,
          provenance: "visual.waistline",
          clear: () => {
            brief.front.waistline = undefined;
            state.form.data.waistline = "";
            setConcepts("waistline", undefined);
          },
        },
        {
          value: brief.back.prompt,
          tombstone: "backPrompt" as const,
          provenance: "visual.backPrompt",
          clear: () => {
            brief.back.prompt = undefined;
            brief.back.details = [];
            state.form.data.backPrompt = "";
            setConcepts("back", undefined);
          },
        },
      ]) {
        clearRejectedScalar(
          candidate.value,
          candidate.tombstone,
          candidate.provenance,
          candidate.clear
        );
      }
      break;
    }
  }

  brief.updatedAt = new Date().toISOString();
  // Presets/widgets are server-authored, one-use capabilities. Their exact
  // data and actionData are matched against the persisted server surface;
  // replacing a DesignSpec here would make an otherwise legitimate click
  // unverifiable. The editable brief/form is the local draft. Render and
  // quote entry points resolve that draft separately at invocation time.
  state.form.showValidationError = false;
}
